/**
 * Onboarding Service
 * Handles API calls for therapist onboarding status
 */

import api from './api'
import { OnboardingStatusResponse } from '../types/onboarding'

export const onboardingService = {
  /**
   * Fetch onboarding status from backend
   * Gets the status from /therapist/profile endpoint
   */
  getOnboardingStatus: async (): Promise<OnboardingStatusResponse> => {
    try {
      const response = await api.get('/therapist/profile')
      const data = response.data?.data || response.data

      // Extract onboarding-related fields
      const status: OnboardingStatusResponse = {
        isConnectBank: data?.isConnectBank ?? false,
        isSubscriptionTaken: data?.isSubscriptionTaken ?? false,
        isServiceCreated: data?.isServiceCreated ?? false,
        isSlotAdded: data?.isSlotAdded ?? false,
      }
      return status
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error)
      // Return default values if API fails
      return {
        isConnectBank: false,
        isSubscriptionTaken: false,
        isServiceCreated: false,
        isSlotAdded: false,
      }
    }
  },

  getOnboardingAgencyStatus: async (): Promise<OnboardingStatusResponse> => {
    try {
      const response = await api.get('/agency/profile')
      const data = response.data?.data || response.data

      // Extract onboarding-related fields
      const status: OnboardingStatusResponse = {
        isConnectBank: data?.isConnectBank ?? false,
        isSubscriptionTaken: data?.isSubscriptionTaken ?? false,
        isServiceCreated: data?.isServiceCreated ?? false,
        isSlotAdded: data?.isSlotAdded ?? false,
      }
      return status
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error)
      // Return default values if API fails
      return {
        isConnectBank: false,
        isSubscriptionTaken: false,
        isServiceCreated: false,
        isSlotAdded: false,
      }
    }
  },

  /**
   * Update a specific onboarding step
   * Note: The backend automatically updates these flags when actions are completed
   * This method is here for manual override if needed
   */
  updateOnboardingStep: async (
    step: keyof OnboardingStatusResponse,
    completed: boolean
  ): Promise<OnboardingStatusResponse> => {
    try {
      // Update the specific field via API
      await api.patch('/therapist/profile', {
        [step]: completed,
      })

      // Fetch and return updated status
      return await onboardingService.getOnboardingStatus()
    } catch (error) {
      console.error(`Failed to update onboarding step ${String(step)}:`, error)
      // Return current status if update fails
      return await onboardingService.getOnboardingStatus()
    }
  },

  updateOnboardingAgencyStep: async (
    step: keyof OnboardingStatusResponse,
    completed: boolean
  ): Promise<OnboardingStatusResponse> => {
    try {
      // Update the specific field via API
      await api.patch('/agency/profile', {
        [step]: completed,
      })

      // Fetch and return updated status
      return await onboardingService.getOnboardingAgencyStatus()
    } catch (error) {
      console.error(`Failed to update onboarding step ${String(step)}:`, error)
      // Return current status if update fails
      return await onboardingService.getOnboardingAgencyStatus()
    }
  },

  /**
   * Check if all mandatory steps are completed
   */
  isOnboardingComplete: (status: OnboardingStatusResponse): boolean => {
    return (
      status.isConnectBank &&
      status.isSubscriptionTaken &&
      status.isServiceCreated &&
      status.isSlotAdded
    )
  },
}
